import {
  useReducer,
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { query } from 'gql-query-builder';

// import firebase from '../../utils/firebase';
import { User } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { Timestamp } from 'firebase/firestore';
import firestoreErrors from '../../utils/firestoreErrors';
import firebase from '../../utils/firebase';
import { error as errorLabels } from '../../label';
import {
  getCompanies,
  getCompany,
  getRefL1ExtractorModules,
  getRefL1ExtractorModuleFields,
  getRefSystemMessages,
  getRefL1ExtractorModuleTemplates,
  getRefCompanySecs,
  getRefGeneralMessages,
  getBatch,
  getRefUsedTokens,
  getRefBonusTokens,
  getRefBonusTokensLogs,
  getRefSupportPermissions,
  getRefSupportPermissionsDoc,
} from '../../utils/firestore';
import { ip, ipAdmin } from '../../utils/functions/urls';
import getRequestMeta from '../../utils/functions/generateMeta';
import denormalizeFields from '../../utils/functions/denormalizeFields';
import { isoDateToUTC } from '../../juristec-ui/utils/functions/lab';

//
import { AuthContext } from '../AuthProvider';
import {
  Action,
  CompanyInfo,
  ReportsFilter,
  InstanceState,
  ETL,
  Report,
  InstanceResponse,
  InfoResponse,
  GedKeyDoc,
  Message,
  Instance,
  ReportsResponse,
  MiddlewareFunction,
  InstanceAPI,
  InstanceContextType,
  PermissionsSupportDoc,
} from './types';
import { SelectOption } from '../../types';

const getToken = async (u: User) => u.getIdToken();

const initialState: InstanceState = {
  instances: [],
  reports: {
    list: [],
    pageTotal: null,
  },
  users: [],
  isLoading: false,
};

const reducer = (state: InstanceState, action: Action) => {
  switch (action.type) {
    case 'setInstances':
      return {
        ...state,
        isLoading: false,
        instances: action.instances,
      };

    case 'setInstancesUsers':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            return {
              ...instance,
              users: action.users,
            };
          }

          return instance;
        }),
        isLoading: false,
      };

    case 'editInstance':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action?.companyinfo?.companyid) {
            return {
              ...instance,
              ...action.companyinfo, // outras variáveis n mudou os nomes
              superS: action.companyinfo.supers,
              volumeData: action.companyinfo.volumedata,
            };
          }

          return instance;
        }),
        isLoading: false,
      };

    case 'blockInstance':
      return {
        ...state,
        isLoading: false,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            return {
              ...instance,
              disabled: action.block,
            };
          }
          return instance;
        }),
      };

    case 'deleteInstance':
      return {
        ...state,
        instances: state.instances.filter((instance) => instance.id !== action.instanceId),
        isLoading: false,
      };

    // users
    case 'blockUser':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            const users = instance.users?.map((u) => (u.email === action.email
              ? {
                ...u,
                disabled: action.newValue,
              } : u));

            return {
              ...instance,
              users,
            };
          }

          return instance;
        }),
        isLoading: false,
      };

    case 'deleteUser': {
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            const users = instance.users?.filter((u) => u.email !== action.email);
            return {
              ...instance,
              users,
            };
          }
          return instance;
        }),
        users: state.users?.filter((u) => u.email !== action.email),
        isLoading: false,
      };
    }

    case 'deactivateUser':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            const users = instance.users?.map(
              (u) => (u.email === action.email ? { ...u, removed: true } : u),
            );
            return {
              ...instance,
              users,
            };
          }
          return instance;
        }),
        users: state.users.map((u) => (u.email === action.email ? { ...u, removed: true } : u)),
        isLoading: false,
      };

    case 'reactivateUser':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            const users = instance.users?.map((u) => (u.email === action.email
              ? {
                ...u,
                removed: false,
              } : u));

            return {
              ...instance,
              users,
            };
          }
          return instance;
        }),
        isLoading: false,
      };

    case 'changeUserRole':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            const users = instance.users?.map((u) => (u.email === action.email ? {
              ...u,
              role: action.newRole,
              scientist: action.scientist,
            } : u));

            return {
              ...instance,
              users,
            };
          }

          return instance;
        }),
      };
    case 'registerTrainingDate':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            const users = instance.users?.map((u) => (u.email === action.email ? {
              ...u,
              trainedAt: action.trainedAt,
            } : u));

            return {
              ...instance,
              users,
            };
          }

          return instance;
        }),
        users: state.users.map(
          (u) => (u.email === action.email ? { ...u, trainedAt: action.trainedAt } : u),
        ),
      };

    case 'newEditUser': {
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            const users = instance.users?.map((u) => (u.uid === action.userId ? {
              ...u,
              ...action.objectAttributes,
            } : u));
            return { ...instance, users };
          }
          return instance;
        }),
        users: state.users.map(
          (u) => (u.uid === action.userId ? { ...u, ...action.objectAttributes } : u),
        ),
      };
    }
    // reports
    case 'setReports': {
      const currentReports = action.reset ? [] : [...state.reports.list];
      currentReports[action.page] = action.reports;

      return {
        ...state,
        isLoading: false,
        reports: {
          list: currentReports,
          pageTotal: action.pageTotal,
        },
      };
    }

    case 'updateReports': {
      const flatArray = state.reports.list.flat(1);
      const updatedArray = flatArray.map((rep) => {
        if (rep.id === action.reportId) {
          return ({
            ...rep,
            lastAttemptStatus: action.updated.status,
            updatedAt: action.updated.attemptAt,
            updatedById: action.updated.byId,
            updatedByEmail: action.updated.byEmail,
            attempt: {
              errors: action.updated.errors,
              message: action.updated.message,
            },
          });
        }
        return rep;
      });
      const resultArray = [];
      while (updatedArray.length > 0) resultArray.push(updatedArray.splice(0, 20));

      return {
        ...state,
        isLoading: false,
        reports: {
          ...state.reports,
          list: resultArray,
        },
      };
    }

    case 'filterReport': {
      const flatArray = state.reports.list.flat(1);
      const filteredArray = flatArray.filter((rep) => rep.id !== action.reportId);
      const page = Math.ceil(filteredArray.length / 20);
      const resultArray = [];
      while (filteredArray.length > 0) resultArray.push(filteredArray.splice(0, 20));

      return {
        ...state,
        isLoading: false,
        reports: {
          ...state.reports,
          list: resultArray,
          pageTotal: page,
        },
      };
    }

    case 'setUsers':
      return {
        ...state,
        isLoading: false,
        users: action.users,
      };

    case 'emptyReportList':
      return {
        ...state,
        reports: {
          ...state.reports,
          list: [],
        },
      };

    case 'setApiGedSuccess':
      return {
        ...state,
        instances: state.instances.map((instance) => {
          if (instance.id === action.instanceId) {
            return {
              ...instance,
              apiGedCredentials: true,
            };
          }
          return instance;
        }),
      };

    case 'setLoading':
      return {
        ...state,
        isLoading: action.isLoading,
      };
    default:
      return state;
  }
};

export const InstanceContext = createContext<InstanceContextType>({
  state: initialState,
  instanceAPI: {} as InstanceAPI,
});

function InstanceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const setLoading = useCallback((l = true) => dispatch({ type: 'setLoading', isLoading: l }), []);

  const { currentUser } = useContext(AuthContext);

  const getInstances = useCallback(async () => {
    const getCompaniesWithSecs = async () => {
      const res = await getCompanies().get();
      const companiesPromises = res.docs.map(async (d) => {
        const secs: string[] = [];
        const companyData = d.data();
        const secsRef = await getRefCompanySecs(d.id).get();
        if (secsRef.size > 0) {
          secsRef.forEach((doc) => {
            secs.push(doc.id);
          });
        }
        const supportPermissionsSnap = await getRefSupportPermissions(d.id)
          .orderBy('validUntil', 'desc')
          .limit(1)
          .get();
        let supportPermissionReports = false;
        let supportPermissionReportsExpiration: string | null = null;
        if (!supportPermissionsSnap.empty) {
          const doc = supportPermissionsSnap.docs[0];
          supportPermissionReports = doc.data().validUntil.toDate() > new Date();
          supportPermissionReportsExpiration = doc.data().validUntil.toDate().toISOString();
        }
        return {
          ...companyData,
          id: d.id,
          apiGedCredentials: secs.includes('l1reports'),
          supportPermissionReports,
          supportPermissionReportsExpiration,
        };
      });
      return Promise.all(companiesPromises);
    };
    try {
      const instances = await getCompaniesWithSecs();
      return { error: false, msg: '', instances };
    } catch (error) {
      const er = error as FirebaseError;
      return {
        error: true,
        msg: firestoreErrors(er.code) || errorLabels.instanceProvider.getInstances,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, []);

  const getInstance = useCallback(async (instanceId: string) => {
    try {
      const res = await getCompany(instanceId).get();
      const instance = { id: res.id, ...res.data() };
      return { error: false, msg: '', instance };
    } catch (error) {
      const er = error as FirebaseError;
      return {
        error: true,
        msg: firestoreErrors(er.code) || errorLabels.instanceProvider.getInstance,
        raw: `Erro do sistema: ${er.toString()}`,

      };
    }
  }, []);

  const getUnique = useCallback(async (database: string, column: string, selector: string = '', filters: Map<string, string>[] = []) => {
    try {
      const token = await getToken(currentUser);
      // const userId = ownerId || currentUser.id || currentUser.uid;
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          report_id: database,
          column,
          selector,
          filters,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/unique`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true, msg: json?.msg || errorLabels.filesProvider.getUnique, raw: json.error,
        };
      }

      return { error: false, msg: null, res: json.info };
    } catch (er: unknown) {
      if (er instanceof Error) {
        console.log(er.message);
        return { error: true, msg: errorLabels.filesProvider.getUnique, raw: `Erro do sistema: ${er.message}` };
      }
      return { error: true, msg: errorLabels.filesProvider.getUnique, raw: 'Unknown error' };
    }
  }, [currentUser]);
  const getInstanceUsers = useCallback(async (instanceId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({ company_id: instanceId }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/list`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return { error: true, msg: errorLabels.instanceProvider.getInstanceUsers, raw: json.error };
      }

      return { error: false, users: json.info.map((u: any) => ({ ...u, instanceId })) };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getInstanceUsers,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const createInstance = useCallback(async (companyinfo: CompanyInfo, emails_super: string[] = [], emails_scientist: string[] = [], image: string = '') => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          ...companyinfo,
          emails_super,
          emails_scientist,
          image,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/instance`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.createInstance,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.createInstance,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const editInstance = useCallback(async (companyinfo: any, image = '') => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          ...companyinfo,
          image,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/instance`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.editInstance,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.editInstance,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const blockInstance = useCallback(async (instanceId: string, block = true) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          company_id: instanceId,
          disabled: block,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/instance`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.blockInstance,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.blockInstance,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const deleteInstance = useCallback(async (instanceId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'DELETE', 'JSON'),
        body: JSON.stringify({ company_id: instanceId }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/instance`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.deleteInstance,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.deleteInstance,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getInstanceTokens = useCallback(async (instanceId: string) => {
    try {
      const docId = new Date().toISOString().replace(/-\d+T.*/, '');
      const usedDoc = await getRefUsedTokens(instanceId, docId).get();
      const bonusDoc = await getRefBonusTokens(instanceId).get();
      return {
        error: false,
        used: usedDoc.data()?.used ?? 0,
        bonus: bonusDoc.data()?.available ?? 0,
      };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.getInstanceTokens,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const addBonusTokens = useCallback(async (
    instanceId: string, tokens: number, responsible: string,
  ) => {
    try {
      const batch = getBatch();
      batch.set(
        getRefBonusTokens(instanceId),
        { available: firebase.increment(tokens) },
        { merge: true },
      );
      batch.set(
        getRefBonusTokensLogs(instanceId).doc(),
        {
          quantidy: tokens,
          timestamp: firebase.serverTimestamp(),
          responsible,
        },
      );
      await batch.commit();
      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.addBonusTokens,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const newEditUser = useCallback(async (
    userId: string, objectAttributes: Record<string, string | number | boolean>,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify(objectAttributes),
      };
      const resFetch = await fetch(`${ipAdmin}/admin/user/${userId}`, opt);

      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.editUser,
          raw: json?.error,
        };
      }
      return { error: false, msg: '', res: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.editUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  // USERS ON INSTANCE
  const blockUser = useCallback(async (
    instanceId: string,
    scientistId: any,
    email: string,
    newValue: any,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          company_id: instanceId,
          scientist_uid: scientistId,
          email,
          field: 'disabled',
          new_value: newValue,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        const parsed = JSON.parse(json);
        return {
          error: true,
          msg: parsed?.errors || errorLabels.instanceProvider.blockInstance,
          raw: json,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.blockInstance,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const multiSessionUser = useCallback(async (
    instanceId: string,
    scientistId: any,
    email: string,
    newValue: any,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          company_id: instanceId,
          scientist_uid: scientistId,
          email,
          field: 'multiSessions',
          new_value: newValue,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.multiSessionUser,
          raw: json?.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.multiSessionUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const sessionTimeoutUser = useCallback(async (
    instanceId: string,
    scientistId: any,
    email: string,
    newValue: number,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          company_id: instanceId,
          scientist_uid: scientistId,
          email,
          field: 'sessionTimeout',
          new_value: newValue,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.sessionTimeoutUser,
          raw: json?.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.sessionTimeoutUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const addUser = useCallback(async (
    instance: string,
    scientist: string = '',
    email: string,
    role: string,
    sendEmail: boolean = true,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          company_id: instance,
          scientist_uid: scientist,
          new_email: email,
          role,
          send_email: sendEmail,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.addUser,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.addUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const deleteUser = useCallback(async (email: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'DELETE', 'JSON'),
        body: JSON.stringify({ email }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.error || errorLabels.instanceProvider.deleteUser,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.deleteUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const deactivateUser = useCallback(async (email: string, emailToTransfer: any) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          email,
          transfer_to_email: emailToTransfer,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/deactivate`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.error || errorLabels.instanceProvider.deactivateUser,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.deactivateUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const reactivateUser = useCallback(async (email: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          email,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/reactivate`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.error || errorLabels.instanceProvider.reactivateUser,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.reactivateUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const resetPwdLink = useCallback(async (email: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          email,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/reset`, opt);
      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.error || errorLabels.instanceProvider.resetPwdLink,
        };
      }
      return { error: false, link: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.resetPwdLink,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const changeUserEmail = useCallback(async (userId: string, newEmail: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({ userId, newEmail }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/email`, opt);
      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.error || errorLabels.instanceProvider.changeUserEmail,
        };
      }
      return { error: false };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.changeUserEmail,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const sendVerificationEmail = useCallback(async (userId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({ userId }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/email`, opt);
      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.error || errorLabels.instanceProvider.sendVerificationEmail,
        };
      }
      return { error: false };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.sendVerificationEmail,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const changeUserRole = useCallback(async (instanceId: string, email: string, newRole: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT'),
        body: JSON.stringify({
          email,
          companyId: instanceId,
          role: newRole,
          update: 'role',
        }),
      };

      const resFetch = await fetch(`${ip}/manage/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        const parsed = JSON.parse(json);
        return {
          error: true,
          msg: parsed?.errors || errorLabels.instanceProvider.editUser,
          raw: json,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.editUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const changeUserRoleByAdmin = useCallback(async (instanceId: string, scientistId: string = '', email: string, newRole: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          email,
          company_id: instanceId,
          scientist_uid: scientistId,
          field: 'role',
          new_value: newRole,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.editUser,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.editUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const transferUserData = useCallback(async (
    instanceId: string, userSender: any, userReceiver: any, viewerOnly = false,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          origin_uid: userSender,
          destination_uid: userReceiver,
          company_uid: instanceId,
          viewer_only: viewerOnly,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/transfer`, opt);
      const json = await resFetch.json();

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.transferUser,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.transferUser,
          raw: json.error,
        };
      }
      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.transferUser,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  // REPORTS
  const addAntecipeiReport = useCallback(async (
    filename: string,
    ownerId: string,
    companyId: string,
    url: any,
    sendWithoutDataProc: any,
    prettyColumns: any,
    reportToken: any,
    onlyActive: boolean,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          predefined_connector: 'Antecipei',
          filename: `${filename}.xlsx`,
          ownerId,
          companyId,
          url: `${url}?send_without_dataproc=${sendWithoutDataProc}&pretty_columns=${prettyColumns}&only_active=${onlyActive}`,
          method: 'GET',
          auth: {
            method: 'x-api-token',
            token: reportToken,
          },
          payload: {},
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/create/web`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.addAntecipeiReport,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.addAntecipeiReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const addBennerReport = useCallback(async (
    filename: string,
    ownerId: string,
    companyId: string,
    url: any,
    username: any,
    password: any,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          predefined_connector: 'Benner',
          filename: `${filename}.xlsx`,
          ownerId,
          companyId,
          url,
          method: 'GET',
          auth: {
            method: 'Basic Auth',
            username,
            password,
          },
          payload: {},
          header_skip: 0,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/create/web`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.addBennerReport,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.addBennerReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const createOnboardingReport = useCallback(async (
    filename: string, instance: string, ownerId: string, fields: SelectOption[], selectedFields: SelectOption[], module: string, alias: Map<string, string>,
  ) => {
    try {
      const nodeFields = denormalizeFields(selectedFields);
      // @ts-ignore
      if (!['id'].includes(nodeFields)) {
        nodeFields.push('id');
      }
      const queryFields = [
        'totalCount',
        {
          pageInfo: ['hasNextPage', 'startCursor', 'endCursor'],
          nodes: [...nodeFields],
        },
      ];
      const queryOptions = {
        operation: module,
        variables: {
          startUpdatedDateUtc: {
            value: isoDateToUTC(new Date(0)),
            type: 'DateTime',
          },
          after: {
            value: null,
            type: 'String',
          },
        },
        fields: queryFields,
      };
      const q = query(queryOptions);
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          filename,
          ownerId,
          method: 'GET',
          predefined_connector: 'LegalOneExtractor',
          payload: {
            query: q,
            l1ReportModule: module,
            l1ReportFields: [...nodeFields],
            alias,
          },
          companyId: instance,
          updatedAt: '',
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/create/web`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.createOnboardingReport,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.createOnboardingReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getDataExtractorInfo = useCallback(async (companyId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          companyId,
          get_defaults: false,
          sort: true,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/company/l1extr/schema`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getDataExtractorInfo,
          raw: json.error,
        };
      }

      return { error: false, msg: '', res: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.getDataExtractorInfo,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getDataExtractorModules = useCallback(async () => {
    try {
      const l1ModulesRef = getRefL1ExtractorModules();
      const data = await l1ModulesRef.get().then((doc) => doc.data())
        .catch((error) => {
          console.log(error);
          return {
            error: true,
            msg: errorLabels.instanceProvider.getDataExtractorModules,
            raw: `Erro ao recuperar dados: ${error.message}`,
          };
        });
      return { error: false, msg: '', res: data };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.getDataExtractorModules,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getL1ReportModuleFields = useCallback(async (module: any, plan: any) => {
    const l1ModulesRef = getRefL1ExtractorModuleFields(module, plan);
    const data = await l1ModulesRef.get().then((doc) => ({ res: doc.data(), error: false, msg: '' }))
      .catch((error) => {
        console.log(error);
        return {
          error: true,
          msg: errorLabels.instanceProvider.getL1ReportModuleFields,
          raw: `Erro do sistema: ${error.toString()}`,
        };
      });
    return data;
  }, [currentUser]);

  const getExtractorPossibleTemplates = useCallback(async (module: any, plan: any) => {
    const l1TemplatesRef = getRefL1ExtractorModuleTemplates(module, plan);
    const data = await l1TemplatesRef.get().then((col) => ({
      res: col.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          value: d.columns,
          label: d.name,
        };
      }),
      error: false,
      msg: '',
    }))
      .catch((error) => {
        console.log(error);
        return {
          error: true,
          msg: errorLabels.instanceProvider.getExtractorPossibleTemplates,
          raw: `Erro do sistema: ${error.toString()}`,
        };
      });
    return data;
  }, [currentUser]);

  const getReports = useCallback(
    async (
      page: number, filters: ReportsFilter, qty: number,
    ): Promise<ReportsResponse> => {
      try {
        const token = await getToken(currentUser);
        const opt = {
          ...await getRequestMeta(token),
        };
        let strFilters = '';
        strFilters += `&active=${!filters?.inactive?.selected}`;
        if (filters?.origin?.selected?.length > 0) strFilters += `&origin=${filters.origin.selected.join(',')}`;
        if (filters?.status?.selected?.length > 0) strFilters += `&status=${filters.status.selected.join(',')}`;
        if (filters?.connector?.selected?.length > 0) strFilters += `&connector=${filters.connector.selected.join(',')}`;
        if (filters?.companyId?.selected?.length > 0) strFilters += `&companyId=${filters.companyId.selected.join(',')}`;
        if (filters?.filename?.selected?.length > 0) strFilters += `&arquivo=${filters.filename.selected}`;
        if (filters?.notUpdatedAt?.selected.length > 0) strFilters += `&notUpdatedAt=${filters.notUpdatedAt.selected[0].split('T')[0]}`;
        const resFetch = await fetch(
        `${ipAdmin}/connect/list?withInfo=true&page=${page + 1}&size=${qty}${strFilters}`, opt,
        );
        const json = await resFetch.json();
        if (resFetch.status >= 500) {
          return {
            error: true,
            msg: errorLabels.instanceProvider.getReports,
            raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
          };
        }
        if (resFetch.status !== 200) {
          return {
            error: true,
            msg: errorLabels.instanceProvider.getReports,
            raw: json.error,
          };
        }
        const reps = Object.keys(json.info.reports).map((k) => (
          {
            ...json.info.reports[k],
            status: json.info.reports[k]?.lastAttemptStatus || 'SUCCESS',
          }
        ));
        return {
          error: false,
          msg: '',
          data: {
            reports: reps,
            pageTotal: json.info.pageTotal,
          },
        };
      } catch (error) {
        const er = error as Error;
        console.log(er);
        return {
          error: true,
          msg: errorLabels.instanceProvider.getReports,
          raw: `Erro do sistema: ${er.toString()}`,
        };
      }
    }, [currentUser],
  );

  const getReportETLs = useCallback(async (hashcode: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token),
      };
      const res = await fetch(`${ipAdmin}/connect/datasource/etl/${hashcode}`, opt);
      const json = await res.json();
      if (res.status >= 400) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getReportEtls,
          raw: json.error,
        };
      }
      return { error: false, msg: '', data: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.getReportEtls,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const toQueryString = (
    params: {
      [x: string]: string | number | boolean | string[];
      test: boolean;
      update_method: string;
      update: boolean;
    } & Partial<ETL>,
  ) => Object.keys(params)
    .map((key) => {
      if (Array.isArray(params[key])) {
        return params[key].map((val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`).join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
    })
    .join('&');

  const getETLQueryString = (
    etl: Partial<ETL>,
    dryRun: boolean,
    update: boolean,
    autoUpdated: boolean,
    activeReportId: string,
  ) => {
    // Include activeReportId if there are other reports in the list (list larger than one)
    let filteredRequiredReportIds = etl?.required_report_ids || [];
    if (
      filteredRequiredReportIds.length > 0
      && activeReportId
      && !filteredRequiredReportIds.includes(activeReportId)) {
      // Prepend the active report ID
      filteredRequiredReportIds = [activeReportId, ...filteredRequiredReportIds];
    }
    const urlQuery = {
      description: etl?.description || '',
      name: etl?.name || '',
      order: etl?.order || 0,
      required_report_ids: filteredRequiredReportIds,
      test: dryRun,
      update_method: 'replace',
      auto_updated: autoUpdated,
      update,
    };

    const queryString = toQueryString(urlQuery);
    return queryString;
  };

  const createETL = useCallback(async (
    report: Report, etl: ETL, dryRun: boolean, update: boolean, autoUpdated: boolean,
  ) => {
    try {
      const token = await getToken(currentUser);
      const data = new FormData();
      const codeBlob = new Blob([etl.code || ''], { type: 'text/x-python' }); // Use 'text/x-python' as the MIME type
      data.append('file', codeBlob, `${etl.name}.py`);

      const opt = {
        ...await getRequestMeta(token, 'POST'),
        body: data,
      };

      const queryString = getETLQueryString(etl, dryRun, update, autoUpdated, report.id);

      const res = await fetch(
        `${ipAdmin}/connect/datasource/etl/${report.id}?${queryString}`, opt,
      );
      if (res.status >= 400) {
        const json = await res.json();
        return {
          error: true,
          msg: errorLabels.instanceProvider.createETL,
          raw: json.error,
        };
      }
      if (dryRun) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${report.filename}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return { error: false, msg: 'File downloaded' };
      }
      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.createETL,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const updateETL = useCallback(async (
    report: Report, etl: Partial<ETL>, dryRun: boolean, update: boolean, autoUpdated: boolean,
  ) => {
    try {
      const token = await getToken(currentUser);
      const data = new FormData();
      const codeBlob = new Blob([etl.code!], { type: 'text/x-python' }); // Use 'text/x-python' as the MIME type
      data.append('file', codeBlob, `${etl.name}.py`);

      const opt = {
        ...await getRequestMeta(token, 'PUT'),
        body: data,
      };

      const queryString = getETLQueryString(etl, dryRun, update, autoUpdated, report.id);
      const res = await fetch(
        `${ipAdmin}/connect/datasource/etl/${report.id}/${etl.id}/code_edit?${queryString}`,
        opt,
      );
      if (res.status >= 400) {
        const json = await res.json();
        return {
          error: true,
          msg: errorLabels.instanceProvider.updateETL,
          raw: json.error,
        };
      }
      if (dryRun) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${report.filename}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return { error: false, msg: 'File downloaded' };
      }
      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.updateETL,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const deleteETL = useCallback(async (report: { id: any; }, etl: { id: any; }) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'DELETE'),
      };

      const res = await fetch(
        `${ipAdmin}/connect/datasource/etl/${report.id}/${etl.id}`, opt,
      );
      if (res.status >= 400) {
        const json = await res.json();
        return {
          error: true,
          msg: errorLabels.instanceProvider.deleteETL,
          raw: json.error,
        };
      }
      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.deleteETL,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getReportStatus = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.deleteReport,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/datasource/${hashcode}`, opt);
      const json = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getReportStatus,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getReportStatus,
          raw: json.error,
        };
      }

      return { error: false, msg: '', data: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.getReportStatus,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const deleteReport = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.deleteReport,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'DELETE', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/datasource/${hashcode}`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.deleteReport,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.deleteReport,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', deletedId: hashcode };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.deleteReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const resetReport = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.resetReport,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/datasource/${hashcode}/reset`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.resetReport,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.resetReport,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', resetId: hashcode };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.resetReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const resetReportAttempts = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.resetReport,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/datasource/${hashcode}/reset-attempts`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.resetReport,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.resetReport,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', resetId: hashcode };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.resetReportAttempts,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const convertToGed = useCallback(async (reportId: any) => {
    try {
      if (!reportId) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.convertToGed,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/admin/datasource/${reportId}/convert`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.convertToGed,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.convertToGed,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', reportId };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.convertToGed,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const genEmptyFile = useCallback(async (
    filename: string,
    companyId: string,
    ownerId: string,
    connector: string,
    origin: string,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          filename,
          companyId,
          ownerId,
          connector,
          origin,
        }),
      };

      const resFetchF = await fetch(`${ipAdmin}/admin/datasource/empty`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.genEmptyFile,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.genEmptyFile,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', hash: jsonF.info.hash };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.genEmptyFile,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const manualReportUpdate = useCallback(async (hashcode: string, origin: string, file: File, filename: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.manualReportUpdate,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }
      const token = await getToken(currentUser);
      let json: InstanceResponse = { info: {} as InfoResponse };
      let resFetch: Response;
      const todayDate = new Date();
      const date = todayDate.toISOString().split('T')[0];

      if (origin === 'FileApi' || origin === 'Desktop') {
        const data = new FormData();
        data.append('file', file, file.name);
        // data.append('overwrite', overwrite);
        const opt = { ...await getRequestMeta(token, 'PUT'), body: data };

        resFetch = await fetch(`${ipAdmin}/connect/datasource/${hashcode}?date=${date}`, opt);
        json = await resFetch.json();
      } else {
        const opt = {
          ...await getRequestMeta(token, 'PUT', 'JSON'),
        };

        resFetch = await fetch(`${ipAdmin}/connect/datasource/${hashcode}?uploaded_file=${filename}.xlsx&date=${date}`, opt);
        json = await resFetch.json();
      }

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.manualReportUpdate,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
          type: 'error',
          auditable: false,
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.manualReportUpdate,
          raw: json.error || json.errors,
          type: 'error',
          auditable: json?.auditable || false,
        };
      }

      return {
        error: false,
        msg: '',
        data: json.info,
        type: json.info.status === 'WARNING' ? 'warning' : 'success',
        auditable: false,
      };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.manualReportUpdate,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const errorReport = useCallback(async (
    hashcode: string,
    origin: string,
    file: File,
    filename: string,
  ) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.manualReportUpdate,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }
      const token = await getToken(currentUser);
      let resFetch: Response;
      const todayDate = new Date();
      const date = todayDate.toISOString().split('T')[0];

      if (origin === 'FileApi' || origin === 'Desktop') {
        const data = new FormData();
        data.append('file', file, file.name);
        // data.append('overwrite', overwrite);
        const opt = { ...await getRequestMeta(token, 'POST'), body: data };

        resFetch = await fetch(`${ipAdmin}/connect/datasource/error_report/${hashcode}?date=${date}`, opt);
      } else {
        const opt = {
          ...await getRequestMeta(token, 'POST', 'JSON'),
        };

        resFetch = await fetch(`${ipAdmin}/connect/datasource/error_report/${hashcode}?uploaded_file=${filename}.xlsx&date=${date}`, opt);
      }
      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.manualReportUpdate,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
          type: 'error',
        };
      }

      if (resFetch.status !== 200) {
        const json = await resFetch.json();
        return {
          error: true,
          msg: errorLabels.instanceProvider.manualReportUpdate,
          raw: json.error || json.errors,
          type: 'error',
        };
      }

      const blob = await resFetch.blob();
      // @ts-ignore
      blob.filename = `Relatório de erros - ${filename}.xlsx`;
      return { error: false, msg: null, res: blob };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.manualReportUpdate,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const inactivateReport = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.inactivateReport,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'DELETE', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/datasource/status/${hashcode}`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.inactivateReport,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.inactivateReport,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', deletedId: hashcode };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.inactivateReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const activateReport = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.activateReport,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/datasource/status/${hashcode}`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.activateReport,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.activateReport,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', deletedId: hashcode };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.activateReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const restoreReport = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.restoreReport,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/datasource/restore/${hashcode}`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.restoreReport,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.restoreReport,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', deletedId: hashcode };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.restoreReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const changeBennerCredentials = useCallback(async (
    hashcode: string,
    password: string,
    url: string | null = null,
    username: string | null = null,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          password,
          url,
          username,
        }),
      };
      const resFetch = await fetch(`${ipAdmin}/connect/datasource/changecredentials/${hashcode}`, opt);

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.changeBennerCredentials,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.changeBennerCredentials,
          raw: json.error,
        };
      }
      return { error: false, msg: null, res: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return { error: true, msg: errorLabels.instanceProvider.changeBennerCredentials, raw: `Erro do sistema: ${er.toString()}` };
    }
  }, [currentUser]);

  const changeOwnerReport = useCallback(async (
    companyId: string,
    fileId: any,
    currentOwner: any,
    newOwner: any,
    removeOldOwner: any,
  ) => {
    try {
      if (!companyId || !fileId || !currentOwner || !newOwner) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.changeOwnerReport,
          raw: 'Informações incorretas! Verifique o companyId, fileId, proprietário original e novo proprietário!',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          companyId,
          file_id: fileId,
          src_uid: currentOwner,
          dst_uid: newOwner,
          src_remove: removeOldOwner,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admdb/file/owner/edit`, opt);
      const jsonF = await resFetch.json();
      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.changeOwnerReport,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.changeOwnerReport,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.changeOwnerReport,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getReportColumns = useCallback(async (hashcode: string) => {
    try {
      const token = await getToken(currentUser);
      // const userId = ownerId || currentUser.id || currentUser.uid;
      const opt = {
        ...await getRequestMeta(token),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/datasource/columns/${hashcode}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.msg || errorLabels.instanceProvider.getReportColumns,
          raw: json.error,
        };
      }

      return {
        error: false,
        msg: null,
        columns: json.columns,
        alias: json.alias,
        custom: json.custom_columns,
        kpis: json?.kpis || [],
      };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.getReportColumns,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const editReportColumns = useCallback(async (hashcode: string, columnMap: any) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          columns: columnMap,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/datasource/columns/${hashcode}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.msg || errorLabels.instanceProvider.editReportColumns,
          raw: json.error,
        };
      }

      return { error: false, msg: null, res: json.columns };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.editReportColumns,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const previewCustomColumns = useCallback(async (hashcode: string, newColumnsList: any) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          newColumns: newColumnsList,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/datasource/customcolumns/preview/${hashcode}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.msg || errorLabels.instanceProvider.previewCustomColumns,
          raw: json.error,
        };
      }

      return { error: false, msg: null, res: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.previewCustomColumns,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const manageCustomColumns = useCallback(async (hashcode: string, newColumnsList: any) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          newColumns: newColumnsList,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/connect/datasource/customcolumns/${hashcode}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      const json = await resFetch.json();

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: json?.msg || errorLabels.instanceProvider.manageCustomColumns,
          raw: json.error,
        };
      }

      return { error: false, msg: null, res: json.info };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.manageCustomColumns,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const requestApiCredentials = useCallback(async (hashcode: string) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.requestApiCredentials,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
      };

      const resFetch = await fetch(`${ip.replace('/analytics', '/l1dataextractor')}/v1/credentials/${hashcode}`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.requestApiCredentials,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.requestApiCredentials,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const saveApiCredentials = useCallback(async (hashcode: string, key: any, secret: any) => {
    try {
      if (!hashcode) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.requestApiCredentials,
          raw: 'Hashcode nulo ou não encontrado',
        };
      }
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          key,
          secret,
        }),
      };

      const resFetch = await fetch(`${ip.replace('/analytics', '/l1dataextractor')}/v1/credentials/${hashcode}`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.saveApiCredentials,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.saveApiCredentials,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const validateApiCredentials = useCallback(async (key: any, secret: any) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          key,
          secret,
        }),
      };

      const resFetch = await fetch(`${ip.replace('/analytics', '/l1dataextractor')}/v1/credentials/test`, opt);
      const json = await resFetch.json();

      if (resFetch.status === 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }

      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.validateApiCredentials,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.validateApiCredentials,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const saveGedCredentials = useCallback(async (
    instanceId: string, key: string, secret: string,
    mode: string, byUser: { email: string; id: string; },
  ) => {
    try {
      const ref = getRefCompanySecs(instanceId).doc('l1reports');
      const keyObj: GedKeyDoc = {
        consumerKey: key,
        consumerSecret: secret,
      };
      if (mode !== 'edit') {
        // @ts-ignore
        keyObj.createdAt = firebase.serverTimestamp();
        keyObj.createdByEmail = byUser.email;
        keyObj.createdById = byUser.id;
        await ref.set(keyObj);
        return { error: false, msg: 'ok' };
      }
      // @ts-ignore
      keyObj.updatedAt = firebase.serverTimestamp();
      keyObj.updatedByEmail = byUser.email;
      keyObj.updatedById = byUser.id;
      await ref.update(keyObj);

      return { error: false, msg: 'ok' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.saveGedCredentials,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const saveSupportPermissions = useCallback(async (
    instanceId: string, byUser: { email: string; id: string; }, validUntil: Date,
  ) => {
    try {
      const ref = getRefSupportPermissionsDoc(instanceId, new Date().toISOString());
      const keyObj: PermissionsSupportDoc = {
        createdAt: firebase.serverTimestamp(),
        createdByEmail: byUser.email,
        createdById: byUser.id,
        validUntil: Timestamp.fromDate(validUntil),
      };
      await ref.set(keyObj);

      return { error: false, msg: 'ok' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.saveSupportPermissions,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const sendMultiInstanceUsersMessage = useCallback(async (
    instances: string[], lvl: string, title: string, showUntil: Date,
    link: string, users: Array<{user_id: string, company: Instance}>, message: string,
  ) => {
    try {
      const batch = getBatch();
      const refGen = getRefGeneralMessages().doc();
      const notificationId = refGen.id;
      if (notificationId === undefined) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.sendUsersMessage,
          raw: 'NotificationId não definido!',
        };
      }
      batch.set(refGen, { message });

      instances.forEach((instanceId: string) => {
        const refInst = getRefSystemMessages(instanceId).doc();
        const usersInst = users.filter((u: { company: Instance; }) => u?.company?.id === instanceId)
          .map((u: { user_id: string; }) => u.user_id);
        const msgObj: Message = {
          notificationId,
          timestamp: firebase.serverTimestamp(),
          users: usersInst,
          usersToView: usersInst,
          level: lvl,
          message,
          title,
          showUntil,
        };
        if (link) msgObj.link = link;
        batch.set(refInst, msgObj);
      });

      await batch.commit();
      return { error: false, msg: 'ok' };
    } catch (error) {
      const er = error as FirebaseError;
      console.log(er);
      return {
        error: true,
        msg: firestoreErrors(er.code) || errorLabels.instanceProvider.sendUsersMessage,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const sendUsersMessage = useCallback(async (
    instanceId: string,
    lvl: any,
    title: string,
    showUntil: Date,
    link: string,
    users: any[],
    message: string,
  ) => {
    try {
      const ref = getRefSystemMessages(instanceId).doc();

      const msgObj: Message = {
        timestamp: firebase.serverTimestamp(),
        users: [],
        usersToView: [],
        level: lvl,
        message,
        title,
        showUntil,
      };

      if (link) msgObj.link = link;

      users.forEach((userId: string) => {
        msgObj.users.push(userId);
        msgObj.usersToView.push(userId);
      });

      await ref.set(msgObj);
      return { error: false, msg: 'ok' };
    } catch (error) {
      const er = error as FirebaseError;
      console.log(er);
      return {
        error: true,
        msg: firestoreErrors(er.code) || errorLabels.instanceProvider.sendUsersMessage,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getUsers = useCallback(async () => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'GET', 'JSON'),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/list`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return { error: true, msg: errorLabels.instanceProvider.getUsers, raw: json.error };
      }
      const usersJson = json.info.map((user: { company: { name: any; }; }) => ({ ...user, companyName: user?.company?.name || '' }));
      return { error: false, users: usersJson };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getUsers,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getUserLoginHistory = useCallback(async (userId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'GET', 'JSON'),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/login_history/${userId}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getUserHistory,
          raw: json.error,
        };
      }

      return { error: false, res: json.info };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getUserHistory,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getUserInfo = useCallback(async (email: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          email,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/info`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getUserInfo,
          raw: json.error,
        };
      }

      return { error: false, res: json.info };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getUserInfo,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getUserDashboards = useCallback(async (userId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'GET', 'JSON'),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/dashs/${userId}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getUserDashboards,
          raw: json.error,
        };
      }

      return { error: false, res: json.info };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getUserDashboards,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getUserFiles = useCallback(async (companyId: string, userId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          companyId,
          ownerId: userId,
          withInfo: true,
          onlyOwner: false,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admdb/list`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getUserFiles,
          raw: json.error,
        };
      }

      return { error: false, res: json.info };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getUserFiles,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getUserDashFiles = useCallback(async (companyId: string, userId: string, dashId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'GET', 'JSON'),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/reports/${userId}&${dashId}&${companyId}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getUserFiles,
          raw: json.error,
        };
      }

      return { error: false, res: json.info };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getUserFiles,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const getDashKpis = useCallback(async (userId: string, dashId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'GET', 'JSON'),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/dashs/${userId}&${dashId}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.getDashKpis,
          raw: json.error,
        };
      }

      return { error: false, res: json.info };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.getDashKpis,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const deleteDashboard = useCallback(async (userId: string, dashId: string) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'DELETE', 'JSON'),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user/dashs/${userId}&${dashId}`, opt);

      if (resFetch.status >= 500) {
        return {
          error: true,
          msg: errorLabels.fetchGeneric,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.deleteDashboard,
          raw: json.error,
        };
      }

      return { error: false, res: json.info };
    } catch (error) {
      const er = error as Error;
      return {
        error: true,
        msg: errorLabels.instanceProvider.deleteDashboard,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const registerTrainingDate = useCallback(async (
    instanceId: string, email: string, date: string,
  ) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
        body: JSON.stringify({
          email,
          company_id: instanceId,
          scientist_uid: '',
          field: 'trainedAt',
          new_value: date,
        }),
      };

      const resFetch = await fetch(`${ipAdmin}/admin/user`, opt);
      const json = await resFetch.json();
      if (resFetch.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.registerTrainingDate,
          raw: json.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.registerTrainingDate,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const inactivateReportsFromCompany = useCallback(async (instanceId: string) => {
    try {
      if (!instanceId) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.inactivateReportsFromCompany,
          raw: 'Id da empresa nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'DELETE', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/company/${instanceId}/status`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.inactivateReportsFromCompany,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.inactivateReportsFromCompany,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', deletedId: instanceId };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.inactivateReportsFromCompany,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const activateReportsFromCompany = useCallback(async (instanceId: string) => {
    try {
      if (!instanceId) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.activateReportsFromCompany,
          raw: 'Id da empresa nulo ou não encontrado',
        };
      }

      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
      };

      const resFetchF = await fetch(`${ipAdmin}/connect/company/${instanceId}/status`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.activateReportsFromCompany,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.activateReportsFromCompany,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '', deletedId: instanceId };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.activateReportsFromCompany,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const removePasswordsFromCompany = useCallback(async (instanceId: string) => {
    try {
      if (!instanceId) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.removePasswordsFromCompany,
          raw: 'Id da empresa nulo ou não encontrado',
        };
      }
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'PUT', 'JSON'),
      };
      const resFetchF = await fetch(`${ipAdmin}/admin/company/${instanceId}/force_sso`, opt);
      const jsonF = await resFetchF.json();
      if (resFetchF.status >= 500) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.removePasswordsFromCompany,
          raw: 'Verifique se existe algum problema com a sua conexão com a internet e tente novamente mais tarde!',
        };
      }
      if (resFetchF.status !== 200) {
        return {
          error: true,
          msg: errorLabels.instanceProvider.removePasswordsFromCompany,
          raw: jsonF.error,
        };
      }

      return { error: false, msg: '' };
    } catch (error) {
      const er = error as Error;
      console.log(er);
      return {
        error: true,
        msg: errorLabels.instanceProvider.removePasswordsFromCompany,
        raw: `Erro do sistema: ${er.toString()}`,
      };
    }
  }, [currentUser]);

  const middleware: MiddlewareFunction = useCallback(async (action) => {
    switch (action.type) {
      case 'getInstances': {
        setLoading(true);
        const res = await getInstances();
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({ type: 'setInstances', instances: res.instances });
        }
        return res;
      }

      case 'getInstance': {
        setLoading(true);
        const res = await getInstance(action.instanceId);
        setLoading(false);
        return res;
      }

      case 'getInstanceUsers': {
        setLoading(true);
        const res = await getInstanceUsers(action.instanceId);
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({ type: 'setInstancesUsers', users: res.users, instanceId: action.instanceId });
        }
        return res;
      }

      case 'getUnique': {
        setLoading(true);
        const {
          database, column, selector, filters,
        } = action;
        const res = await getUnique(database, column, selector, filters);
        setLoading(false);
        return res;
      }

      case 'getUsers': {
        setLoading(true);
        const res = await getUsers();
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({ type: 'setUsers', users: res.users });
        }
        return res;
      }

      case 'createInstance': {
        setLoading(true);
        const res = await createInstance(action.companyinfo, action.emails, action.image);
        setLoading(false);
        // if(res.error) {
        //   setLoading(false);
        // }
        return res;
      }

      case 'editInstance': {
        setLoading(true);
        const res = await editInstance(action.companyinfo);
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({ type: 'editInstance', companyinfo: action.companyinfo });
        }
        return res;
      }

      case 'blockInstance': {
        setLoading(true);
        const res = await blockInstance(action.instanceId, action.block);
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({ type: 'blockInstance', instanceId: action.instanceId, block: action.block });
        }
        return res;
      }

      case 'deleteInstance': {
        setLoading(true);
        const res = await deleteInstance(action.instanceId);
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({ type: 'deleteInstance', instanceId: action.instanceId });
        }
        return res;
      }

      case 'getInstanceTokens': {
        setLoading(true);
        const res = await getInstanceTokens(action.instanceId);
        setLoading(false);
        return res;
      }

      case 'addBonusTokens': {
        setLoading(true);
        const res = await addBonusTokens(
          action.instanceId, action.tokens, action.responsible,
        );
        setLoading(false);
        return res;
      }

      // USERS
      case 'blockUser': {
        setLoading(true);
        const res = await blockUser(
          action.instanceId,
          action.scientistId,
          action.email,
          action.newValue,
        );
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({
            type: 'blockUser',
            email: action.email,
            instanceId: action.instanceId,
            scientistId: action.scientistId,
            newValue: action.newValue,
          });
        }
        return res;
      }

      case 'multiSessionUser': {
        setLoading(true);
        const res = await multiSessionUser(
          action.instanceId,
          action.scientistId,
          action.email,
          action.newValue,
        );
        setLoading(false);
        return res;
      }

      case 'sessionTimeoutUser': {
        setLoading(true);
        const res = await sessionTimeoutUser(
          action.instanceId,
          action.scientistId,
          action.email,
          action.newValue,
        );
        setLoading(false);
        return res;
      }

      case 'addUser': {
        setLoading(true);
        const res = await addUser(
          action.instance,
          action.scientist,
          action.email,
          action.role,
          action.sendEmail,
        );
        setLoading(false);
        return res;
      }

      case 'deleteUser': {
        setLoading(true);
        const res = await deleteUser(action.email);
        if (!res.error) {
          dispatch({
            type: 'deleteUser', instanceId: action.instanceId, email: action.email,
          });
        }
        setLoading(false);
        return res;
      }

      case 'deactivateUser': {
        setLoading(true);
        const res = await deactivateUser(action.email, action.transferToEmail);
        if (!res.error) {
          dispatch({
            type: 'deactivateUser', instanceId: action.instanceId, email: action.email, emailToTransfer: action.transferToEmail,
          });
        }
        setLoading(false);
        return res;
      }

      case 'registerTrainingDate': {
        setLoading(true);
        const res = await registerTrainingDate(action.instanceId, action.email, action.date);
        if (!res.error) {
          dispatch(
            {
              type: 'registerTrainingDate',
              instanceId: action.instanceId,
              email: action.email,
              trainedAt: action.date,
            },
          );
        }
        setLoading(false);
        return res;
      }
      case 'reactivateUser': {
        setLoading(true);
        const res = await reactivateUser(action.email);
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({
            type: 'reactivateUser', email: action.email, intanceId: action.instanceId,
          });
        }
        return res;
      }

      case 'resetUserPassword': {
        setLoading(true);
        const res = await resetPwdLink(action.email);
        setLoading(false);
        return res;
      }

      case 'changeUserEmail': {
        setLoading(true);
        const res = await changeUserEmail(action.userId, action.newEmail);
        setLoading(false);
        return res;
      }

      case 'sendVerificationEmail': {
        setLoading(true);
        const res = await sendVerificationEmail(action.userId);
        setLoading(false);
        return res;
      }

      case 'changeUserRole': {
        setLoading(true);
        const {
          instanceId, email, newRole,
        } = action;
        const res = await changeUserRole(instanceId, email, newRole);
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({
            type: 'changeUserRole',
            instanceId,
            email,
            newRole,
          });
        }

        return res;
      }

      case 'changeUserRoleByAdmin': {
        setLoading(true);
        const {
          instanceId, scientistId, email, newRole,
        } = action;
        const res = await changeUserRoleByAdmin(instanceId, scientistId, email, newRole);
        setLoading(false);
        return res;
      }

      case 'transferUserData': {
        setLoading(true);
        const res = await transferUserData(
          action.instanceId, action.userSender, action.userReceiver, action.viewerOnly,
        );
        setLoading(false);
        return res;
      }

      // REPORTS
      case 'addAntecipeiReport': {
        setLoading(true);
        const res = await addAntecipeiReport(
          action.filename,
          action.ownerId,
          action.companyId,
          action.url,
          action.sendWithoutDataProc,
          action.prettyColumns,
          action.reportToken,
          action.onlyActive,
        );
        setLoading(false);
        dispatch({ type: 'emptyReportList' });
        return res;
      }

      case 'addBennerReport': {
        setLoading(true);
        const res = await addBennerReport(
          action.filename,
          action.ownerId,
          action.companyId,
          action.url,
          action.username,
          action.password,
        );
        setLoading(false);
        dispatch({ type: 'emptyReportList' });
        return res;
      }

      case 'createOnboardingReport': {
        setLoading(true);
        const res = await createOnboardingReport(
          action.filename,
          action.instance,
          action.ownerId,
          action.fields,
          action.selectedFields,
          action.module,
          action.alias,
        );
        setLoading(false);
        dispatch({ type: 'emptyReportList' });
        return res;
      }

      case 'getDataExtractorInfo': {
        setLoading(true);
        const res = await getDataExtractorInfo(action.companyId);
        setLoading(false);
        return res;
      }

      case 'getDataExtractorModules': {
        setLoading(true);
        const res = await getDataExtractorModules();
        setLoading(false);
        return res;
      }

      case 'getL1ReportModuleFields': {
        setLoading(true);
        const res = await getL1ReportModuleFields(action.module, action.plan);
        setLoading(false);
        return res;
      }

      case 'getExtractorPossibleTemplates': {
        setLoading(true);
        const res = await getExtractorPossibleTemplates(action.module, action.plan);
        setLoading(false);
        return res;
      }

      case 'getReports': {
        setLoading(true);
        const res = await getReports(action.page, action.filters, action.qty);
        if (res.error || action?.dontSetState) {
          setLoading(false);
        } else {
          dispatch({
            type: 'setReports',
            reports: res.data!.reports,
            page: action.page,
            pageTotal: res.data!.pageTotal,
            reset: action.reset,
          });
        }
        return res;
      }

      case 'resetReportAttempts': {
        setLoading(true);
        const res = await resetReportAttempts(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'getReportETLs': {
        setLoading(true);
        const res = await getReportETLs(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'createETL': {
        setLoading(true);
        const res = await createETL(
          action.report, action.etl, action.dryRun, action.update, action.autoUpdated,
        );
        setLoading(false);
        return res;
      }

      case 'updateETL': {
        setLoading(true);
        const res = await updateETL(
          action.report, action.etl, action.dryRun, action.update, action.autoUpdated,
        );
        setLoading(false);
        return res;
      }

      case 'deleteETL': {
        setLoading(true);
        const res = await deleteETL(action.report, action.etl);
        setLoading(false);
        return res;
      }

      case 'getReportStatus': {
        setLoading(true);
        const res = await getReportStatus(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'deleteReport': {
        setLoading(true);
        const res = await deleteReport(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'resetReport': {
        setLoading(true);
        const res = await resetReport(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'convertToGed': {
        setLoading(true);
        const res = await convertToGed(action.reportId);
        setLoading(false);
        return res;
      }

      case 'genEmptyFile': {
        setLoading(true);
        const res = await genEmptyFile(
          action.filename, action.companyId, action.ownerId, action.connector, action.origin,
        );
        setLoading(false);
        return res;
      }

      case 'manualReportUpdate': {
        setLoading(true);
        const res = await manualReportUpdate(
          action.hashcode, action.origin, action.file, action.filename,
        );
        if (res.error) {
          setLoading(false);
        } else {
          dispatch({
            type: 'updateReports',
            updated: res.data,
            reportId: action.hashcode,
          });
        }
        return res;
      }

      case 'changeBennerCredentials': {
        setLoading(true);
        const res = await changeBennerCredentials(
          action.hashcode, action.password, action.url, action.username,
        );
        setLoading(false);
        return res;
      }

      case 'errorReport': {
        setLoading(true);
        const res = await errorReport(action.hashcode, action.origin, action.file, action.filename);
        setLoading(false);
        return res;
      }

      case 'inactivateReport': {
        setLoading(true);
        const res = await inactivateReport(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'activateReport': {
        setLoading(true);
        const res = await activateReport(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'restoreReport': {
        setLoading(true);
        const res = await restoreReport(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'changeOwnerReport': {
        setLoading(true);
        const res = await changeOwnerReport(
          action.companyId,
          action.fileId,
          action.currentOwner,
          action.newOwner,
          action.removeOldOwner,
        );
        setLoading(false);
        return res;
      }

      case 'getReportColumns': {
        setLoading(true);
        const res = await getReportColumns(action.hashcode);
        setLoading(false);
        return res;
      }
      case 'editReportColumns': {
        setLoading(true);
        const res = await editReportColumns(action.hashcode, action.columnMap);
        setLoading(false);
        return res;
      }

      case 'previewCustomColumns': {
        setLoading(true);
        const res = await previewCustomColumns(action.hashcode, action.newColumnsList);
        setLoading(false);
        return res;
      }

      case 'manageCustomColumns': {
        setLoading(true);
        const res = await manageCustomColumns(action.hashcode, action.newColumnsList);
        setLoading(false);
        return res;
      }

      case 'requestApiCredentials': {
        setLoading(true);
        const res = await requestApiCredentials(action.hashcode);
        setLoading(false);
        return res;
      }

      case 'saveApiCredentials': {
        setLoading(true);
        const res = await saveApiCredentials(action.hashcode, action.key, action.secret);
        setLoading(false);
        return res;
      }

      case 'validateApiCredentials': {
        setLoading(true);
        const res = await validateApiCredentials(action.key, action.secret);
        setLoading(false);
        return res;
      }

      case 'saveGedCredentials': {
        setLoading(true);
        const res = await saveGedCredentials(
          action.instanceId, action.key, action.secret, action.mode, action.byUser,
        );
        if (!res.error) {
          dispatch({ type: 'setApiGedSuccess', instanceId: action.instanceId });
        }
        setLoading(false);
        return res;
      }

      case 'saveSupportPermissions': {
        setLoading(true);
        const res = await saveSupportPermissions(
          action.instanceId, action.byUser, action.validUntil,
        );
        setLoading(false);
        return res;
      }

      case 'sendMultiInstanceUsersMessage': {
        setLoading(true);
        const res = await sendMultiInstanceUsersMessage(
          action.instances,
          action.lvl,
          action.title,
          action.showUntil,
          action.link,
          action.users,
          action.message,
        );
        setLoading(false);
        return res;
      }

      case 'sendUsersMessage': {
        setLoading(true);
        const res = await sendUsersMessage(
          action.instanceId,
          action.lvl,
          action.title,
          action.showUntil,
          action.link,
          action.users,
          action.message,
        );
        setLoading(false);
        return res;
      }

      case 'getUserLoginHistory': {
        setLoading(true);
        const res = await getUserLoginHistory(action.userId);
        setLoading(false);
        return res;
      }

      case 'getUserInfo': {
        setLoading(true);
        const res = await getUserInfo(action.email);
        setLoading(false);
        return res;
      }

      case 'getUserDashboards': {
        setLoading(true);
        const res = await getUserDashboards(action.userId);
        setLoading(false);
        return res;
      }

      case 'getUserFiles': {
        setLoading(true);
        const res = await getUserFiles(action.companyId, action.userId);
        setLoading(false);
        return res;
      }

      case 'getUserDashFiles': {
        setLoading(true);
        const res = await getUserDashFiles(action.companyId, action.userId, action.dashId);
        setLoading(false);
        return res;
      }

      case 'getDashKpis': {
        setLoading(true);
        const res = await getDashKpis(action.userId, action.dashId);
        setLoading(false);
        return res;
      }

      case 'deleteDashboard': {
        setLoading(true);
        const res = await deleteDashboard(action.userId, action.dashId);
        setLoading(false);
        return res;
      }

      case 'newEditUser': {
        setLoading(true);
        const {
          userId, objectAttributes,
        } = action;
        const {
          error, msg, raw,
        } = await newEditUser(userId, objectAttributes);
        if (!error) {
          dispatch({
            type: 'newEditUser', userId, objectAttributes,
          });
        }
        setLoading(false);
        return { error, msg, raw };
      }

      case 'inactivateReportsFromCompany': {
        setLoading(true);
        const res = await inactivateReportsFromCompany(action.instanceId);
        setLoading(false);
        return res;
      }

      case 'activateReportsFromCompany': {
        setLoading(true);
        const res = await activateReportsFromCompany(action.instanceId);
        setLoading(false);
        return res;
      }

      case 'removePasswordsFromCompany': {
        setLoading(true);
        const res = await removePasswordsFromCompany(action.instanceId);
        setLoading(false);
        return res;
      }

      default:
        dispatch(action);
        return { error: false, msg: 'default' };
    }
  },
  [
    getInstances,
    createInstance,
    editInstance,
    blockInstance,
    deleteInstance,
    getInstanceTokens,
    addBonusTokens,
    // users
    blockUser,
    multiSessionUser,
    sessionTimeoutUser,
    addUser,
    deleteUser,
    deactivateUser,
    reactivateUser,
    resetPwdLink,
    changeUserRole,
    changeUserRoleByAdmin,
    transferUserData,
    getUsers,
    getUserLoginHistory,
    getUserInfo,
    getUserDashboards,
    getUserFiles,
    getUserDashFiles,
    getDashKpis,
    deleteDashboard,
    newEditUser,
    // reports
    addAntecipeiReport,
    addBennerReport,
    createOnboardingReport,
    getDataExtractorInfo,
    getDataExtractorModules,
    getL1ReportModuleFields,
    getExtractorPossibleTemplates,
    getInstanceUsers,
    getInstance,
    getReports,
    getReportETLs,
    createETL,
    updateETL,
    deleteETL,
    getReportStatus,
    deleteReport,
    resetReport,
    resetReportAttempts,
    convertToGed,
    genEmptyFile,
    manualReportUpdate,
    errorReport,
    inactivateReport,
    activateReport,
    restoreReport,
    changeOwnerReport,
    getReportColumns,
    editReportColumns,
    previewCustomColumns,
    manageCustomColumns,
    changeBennerCredentials,
    //
    requestApiCredentials,
    saveApiCredentials,
    validateApiCredentials,
    saveGedCredentials,
    saveSupportPermissions,
    sendMultiInstanceUsersMessage,
    sendUsersMessage,
    registerTrainingDate,
    setLoading,
    inactivateReportsFromCompany,
    activateReportsFromCompany,
    removePasswordsFromCompany,
  ]);

  const instanceAPI: InstanceAPI = useMemo(() => ({
    getInstances: async () => middleware({ type: 'getInstances' }),
    getInstance: async (instanceId: string) => middleware({ type: 'getInstance', instanceId }),
    getUnique: async (
      database: string, column: string, selector: string, filters: Map<string, string>[],
    ) => middleware({
      type: 'getUnique', database, column, selector, filters,
    }),
    getInstanceUsers: async (instanceId: string) => middleware({ type: 'getInstanceUsers', instanceId }),
    createInstance: async (companyinfo: CompanyInfo, emails: string[], image: any) => middleware({
      type: 'createInstance', companyinfo, emails, image,
    }),
    editInstance: async (companyinfo: CompanyInfo) => middleware({ type: 'editInstance', companyinfo }),
    blockInstance: async (instanceId: string, block: boolean) => middleware({ type: 'blockInstance', instanceId, block }),
    deleteInstance: async (instanceId: string) => middleware({ type: 'deleteInstance', instanceId }),
    getInstanceTokens: async (instanceId: string) => middleware({ type: 'getInstanceTokens', instanceId }),
    addBonusTokens: async (instanceId: string, tokens: number, responsible: string) => middleware({
      type: 'addBonusTokens', instanceId, tokens, responsible,
    }),
    //
    blockUser: async (
      instanceId: string, scientistId: string, email: string, newValue: boolean,
    ) => middleware({
      type: 'blockUser', instanceId, scientistId, email, newValue,
    }),
    multiSessionUser: async (
      instanceId: string, scientistId: string, email: string, newValue: boolean,
    ) => middleware({
      type: 'multiSessionUser', instanceId, scientistId, email, newValue,
    }),
    sessionTimeoutUser: async (
      instanceId: string, scientistId: string, email: string, newValue: number,
    ) => middleware({
      type: 'sessionTimeoutUser', instanceId, scientistId, email, newValue,
    }),
    deleteUser: async (instanceId: string, email: string) => middleware({
      type: 'deleteUser', instanceId, email,
    }),
    deactivateUser: async (
      instanceId: string, email: string, transferToEmail: string,
    ) => middleware({
      type: 'deactivateUser', instanceId, email, transferToEmail,
    }),
    reactivateUser: async (email: string, instanceId: string) => middleware({
      type: 'reactivateUser', email, instanceId,
    }),
    resetPwdLink: async (email: string) => middleware({ type: 'resetUserPassword', email }),
    changeUserEmail: async (userId: string, newEmail: string) => middleware({
      type: 'changeUserEmail', userId, newEmail,
    }),
    sendVerificationEmail: async (userId: string) => middleware({
      type: 'sendVerificationEmail', userId,
    }),
    addUser: async (
      instance: string, scientist: string, email: string, role: string, sendEmail: boolean,
    ) => middleware({
      type: 'addUser', instance, scientist, email, role, sendEmail,
    }),
    getUsers: async () => middleware({ type: 'getUsers' }),
    getUserLoginHistory: async (userId: string) => middleware({
      type: 'getUserLoginHistory', userId,
    }),
    getUserInfo: async (email: string) => middleware({
      type: 'getUserInfo', email,
    }),
    getUserDashboards: async (userId: string) => middleware({
      type: 'getUserDashboards', userId,
    }),
    getUserFiles: async (companyId: string, userId: string) => middleware({
      type: 'getUserFiles', companyId, userId,
    }),
    getUserDashFiles: async (companyId: string, userId: string, dashId: string) => middleware({
      type: 'getUserDashFiles', companyId, userId, dashId,
    }),
    getDashKpis: async (userId: string, dashId: string) => middleware({
      type: 'getDashKpis', userId, dashId,
    }),
    deleteDashboard: async (userId: string, dashId: string) => middleware({
      type: 'deleteDashboard', userId, dashId,
    }),
    registerTrainingDate: async (instanceId: string, email: string, date: string) => middleware({
      type: 'registerTrainingDate', instanceId, email, date,
    }),
    newEditUser: async (
      userId: string, objectAttributes: Record<string, string>,
    ) => middleware({
      type: 'newEditUser', userId, objectAttributes,
    }),
    addAntecipeiReport: async (
      filename: string,
      ownerId: string,
      companyId: string,
      url: string,
      sendWithoutDataProc: boolean,
      prettyColumns: boolean,
      reportToken: string,
      onlyActive: boolean,
    ) => middleware({
      type: 'addAntecipeiReport',
      filename,
      ownerId,
      companyId,
      url,
      sendWithoutDataProc,
      prettyColumns,
      reportToken,
      onlyActive,
    }),
    addBennerReport: async (
      filename: string,
      ownerId: string,
      companyId: string,
      url: string,
      username: string,
      password: string,
    ) => middleware({
      type: 'addBennerReport',
      filename,
      ownerId,
      companyId,
      url,
      username,
      password,
    }),
    createOnboardingReport: async (
      filename: string,
      instance: string,
      ownerId: string,
      fields: SelectOption[],
      selectedFields: SelectOption[],
      module: string,
      alias: Map<string, string>,
    ) => middleware({
      type: 'createOnboardingReport',
      filename,
      instance,
      ownerId,
      fields,
      selectedFields,
      module,
      alias,
    }),
    getDataExtractorInfo: async (companyId: string) => middleware({
      type: 'getDataExtractorInfo', companyId,
    }),
    getDataExtractorModules: async () => middleware({
      type: 'getDataExtractorModules',
    }),
    getL1ReportModuleFields: async (module: string, plan: string) => middleware({
      type: 'getL1ReportModuleFields', module, plan,
    }),
    getExtractorPossibleTemplates: async (module: string, plan: string) => middleware({
      type: 'getExtractorPossibleTemplates', module, plan,
    }),
    changeUserRole: async (instanceId: string, email: string, newRole: string) => middleware({
      type: 'changeUserRole', instanceId, email, newRole,
    }),
    changeUserRoleByAdmin: async (instanceId: string, scientistId: string, email: string, newRole: string) => middleware({
      type: 'changeUserRoleByAdmin', instanceId, scientistId, email, newRole,
    }),
    transferUserData: async (instanceId: string, userSender: string, userReceiver: string, viewerOnly: boolean) => middleware({
      type: 'transferUserData', instanceId, userSender, userReceiver, viewerOnly,
    }),
    getReports: async (page: number, filters: ReportsFilter, qty: number, reset: boolean, dontSetState: boolean = false) => middleware({
      type: 'getReports', page, filters, qty, reset, dontSetState,
    }),
    getReportETLs: async (hashcode: string) => middleware({
      type: 'getReportETLs', hashcode,
    }),
    createETL: async (
      report: Report, etl: ETL, dryRun: boolean, update: boolean, autoUpdated: boolean,
    ) => middleware({
      type: 'createETL', report, etl, dryRun, update, autoUpdated,
    }),
    updateETL: async (
      report: Report, etl: ETL, dryRun: boolean, update: boolean, autoUpdated: boolean,
    ) => middleware({
      type: 'updateETL', report, etl, dryRun, update, autoUpdated,
    }),
    deleteETL: async (report: Report, etl: ETL) => middleware({
      type: 'deleteETL', report, etl,
    }),
    getReportStatus: async (hashcode: string) => middleware({
      type: 'getReportStatus', hashcode,
    }),
    deleteReport: async (hashcode: string) => middleware({
      type: 'deleteReport', hashcode,
    }),
    resetReport: async (hashcode: string) => middleware({
      type: 'resetReport', hashcode,
    }),
    resetReportAttempts: async (hashcode: string) => middleware({
      type: 'resetReportAttempts', hashcode,
    }),
    convertToGed: async (reportId: any) => middleware({
      type: 'convertToGed', reportId,
    }),
    genEmptyFile: async (
      filename: string,
      companyId: string,
      ownerId: string,
      connector: string,
      origin: string,
    ) => middleware({
      type: 'genEmptyFile',
      filename,
      companyId,
      ownerId,
      connector,
      origin,
    }),
    manualReportUpdate: async (hashcode: string, origin: string, file: File, filename: string) => middleware({
      type: 'manualReportUpdate', hashcode, origin, file, filename,
    }),
    errorReport: async (hashcode: string, origin: string, file: File, filename: string) => middleware({
      type: 'errorReport', hashcode, origin, file, filename,
    }),
    inactivateReport: async (hashcode: string) => middleware({
      type: 'inactivateReport', hashcode,
    }),
    activateReport: async (hashcode: string) => middleware({
      type: 'activateReport', hashcode,
    }),
    restoreReport: async (hashcode: string) => middleware({
      type: 'restoreReport', hashcode,
    }),
    changeBennerCredentials: async (
      hashcode: string,
      password: string,
      url: string | null,
      username: string | null,
    ) => middleware({
      type: 'changeBennerCredentials', hashcode, password, url, username,
    }),
    //
    //
    changeOwnerReport: async (
      companyId: string,
      fileId: any,
      currentOwner: any,
      newOwner: any,
      removeOldOwner: any,
    ) => middleware({
      type: 'changeOwnerReport',
      companyId,
      fileId,
      currentOwner,
      newOwner,
      removeOldOwner,
    }),
    getReportColumns: async (hashcode: string) => middleware({
      type: 'getReportColumns', hashcode,
    }),
    editReportColumns: async (hashcode: string, columnMap: any) => middleware({
      type: 'editReportColumns', hashcode, columnMap,
    }),
    previewCustomColumns: async (hashcode: string, newColumnsList: any) => middleware({
      type: 'previewCustomColumns', hashcode, newColumnsList,
    }),
    manageCustomColumns: async (hashcode: string, newColumnsList: any) => middleware({
      type: 'manageCustomColumns', hashcode, newColumnsList,
    }),
    //
    requestApiCredentials: async (hashcode: string) => middleware({
      type: 'requestApiCredentials', hashcode,
    }),
    saveApiCredentials: async (hashcode: string, key: any, secret: any) => middleware({
      type: 'saveApiCredentials', hashcode, key, secret,
    }),
    validateApiCredentials: async (key: any, secret: any) => middleware({
      type: 'validateApiCredentials', key, secret,
    }),
    saveGedCredentials: async (instanceId: string, key: string, secret: string, mode: string, byUser: any) => middleware({
      type: 'saveGedCredentials', instanceId, key, secret, mode, byUser,
    }),
    saveSupportPermissions: async (instanceId: string, byUser: any, validUntil: Date) => middleware({
      type: 'saveSupportPermissions', instanceId, byUser, validUntil,
    }),
    sendMultiInstanceUsersMessage: async (
      instances: any, lvl: any, title: string, showUntil: Date, link: string, users: any, message: string,
    ) => middleware({
      type: 'sendMultiInstanceUsersMessage', instances, lvl, title, showUntil, link, users, message,
    }),
    sendUsersMessage: async (
      instanceId: string,
      lvl: any,
      title: string,
      showUntil: Date,
      link: string,
      users: any,
      message: string,
    ) => middleware({
      type: 'sendUsersMessage', instanceId, lvl, title, showUntil, link, users, message,
    }),
    inactivateReportsFromCompany: async (
      instanceId: string,
    ) => middleware({
      type: 'inactivateReportsFromCompany', instanceId,
    }),
    activateReportsFromCompany: async (
      instanceId: string,
    ) => middleware({
      type: 'activateReportsFromCompany', instanceId,
    }),
    removePasswordsFromCompany: async (
      instanceId: string,
    ) => middleware({
      type: 'removePasswordsFromCompany', instanceId,
    }),
  }), [middleware]);

  return (
    <InstanceContext.Provider value={{ state, instanceAPI }}>
      {children}
    </InstanceContext.Provider>
  );
}

export default InstanceProvider;
